/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { isAfter, isBefore, getDay, getYear, getMonth, getDaysInMonth, getDate, addMonths, addYears, addDays } from 'date-fns';

import { Directionality } from '@angular/cdk/bidi';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    output,
    viewChild,
    input,
    inject
} from '@angular/core';

import { ApDateService } from '@ap/date';

import { ApCalendarBodyComponent, ApCalendarCell } from '../body/calendar-body.component';

const DAYS_PER_WEEK = 7;

/**
 * An internal component used to display a single month in the datepicker.
 *
 * @docs-private
 */
@Component({
    selector: 'ap-month-view',
    templateUrl: './month-view.component.html',
    styleUrls: ['../calendar.component.scss', './month-view.component.scss'],
    exportAs: 'apMonthView',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ApCalendarBodyComponent]
})
export class ApMonthViewComponent implements AfterContentInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly dir = inject(Directionality, { optional: true });

    /**
     * The date to display in this month view (everything other than the month and year is ignored).
     */
    @Input()
    get activeDate(): Date {
        return this._activeDate;
    }

    set activeDate(value: Date) {
        const oldActiveDate = this._activeDate;
        const validDate = ApDateService.getValidDateOrNull(ApDateService.utc(value)) || ApDateService.utc();
        this._activeDate = ApDateService.clampDate(validDate, this.minDate(), this.maxDate());
        if (!this._hasSameMonthAndYear(oldActiveDate, this._activeDate)) {
            this._init();
            this.activeDateChange.emit(this._activeDate);
        }
    }

    /** The currently selected date. */
    @Input()
    get selected(): Date | null {
        return this._selected;
    }

    set selected(value: Date | null) {
        this._selected = ApDateService.getValidDateOrNull(ApDateService.utc(value));
        this._selectedDate = this._getDateInCurrentMonth(this._selected);
    }

    /** The minimum selectable date. */
    public readonly minDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** The maximum selectable date. */
    public readonly maxDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** A function used to filter which dates are selectable. */
    readonly dateFilter = input<(date: Date) => boolean>(undefined);
    /** Emits when a new date is selected. */
    readonly selectedChange = output<Date | null>();
    /** Emits when any date is selected. */
    readonly _userSelection = output<void>();
    /** Emits when any date is activated. */
    readonly activeDateChange = output<Date>();
    /** The body of calendar table */
    readonly _apCalendarBody = viewChild(ApCalendarBodyComponent);
    /** The label for this month (e.g. "January 2017"). */
    _monthLabel: string;
    /** Grid of calendar cells representing the dates of the month. */
    _weeks: ApCalendarCell[][];
    /** The number of blank cells in the first row before the 1st of the month. */
    _firstWeekOffset: number;
    /**
     * The date of the month that the currently selected Date falls on.
     * Null if the currently selected Date is in another month.
     */
    _selectedDate: number | null;
    /** The date of the month that today falls on. Null if today is in another month. */
    _todayDate: number | null;
    /** The names of the weekdays. */
    _weekdays: { long: string; narrow: string }[];

    private _activeDate: Date;

    private _selected: Date | null;

    private readonly firstDayOfWeek = 1;
    private readonly narrowWeekdays = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    private readonly longWeekdays = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
    private readonly monthNames = ['jan.', 'feb.', 'mrt.', 'apr.', 'mei', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];

    public getDate = getDate;

    constructor() {
        // Rotate the labels for days of the week based on the configured first day of the week.
        const weekdays = this.longWeekdays.map((long, i) => ({ long, narrow: this.narrowWeekdays[i] }));
        this._weekdays = weekdays.slice(this.firstDayOfWeek).concat(weekdays.slice(0, this.firstDayOfWeek));

        this._activeDate = ApDateService.utc();
    }

    ngAfterContentInit() {
        this._init();
    }

    /** Handles when a new date is selected. */
    _dateSelected(date: number) {
        if (this._selectedDate !== date) {
            const selectedYear = getYear(this.activeDate);
            const selectedMonth = getMonth(this.activeDate);
            const selectedDate = ApDateService.utc(new Date(selectedYear, selectedMonth, date));

            this.selectedChange.emit(selectedDate);
        }

        this._userSelection.emit(undefined);
    }

    /** Handles keydown events on the calendar body when calendar is in month view. */
    _handleCalendarBodyKeydown(event: KeyboardEvent): void {
        // TODO(mmalerba): We currently allow keyboard navigation to disabled dates, but just prevent
        // disabled ones from being selected. This may not be ideal, we should look into whether
        // navigation should skip over disabled dates, and if so, how to implement that efficiently.
        const isRtl = this._isRtl();
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
        // tslint:disable-next-line:deprecation
        const dateFilter = this.dateFilter();
        switch (event.key) {
            case 'ArrowLeft':
                this.activeDate = addDays(this._activeDate, isRtl ? 1 : -1);
                break;
            case 'ArrowRight':
                this.activeDate = addDays(this._activeDate, isRtl ? -1 : 1);
                break;
            case 'ArrowUp':
                this.activeDate = addDays(this._activeDate, -7);
                break;
            case 'ArrowDown':
                this.activeDate = addDays(this._activeDate, 7);
                break;
            case 'Home':
                this.activeDate = addDays(this._activeDate, 1 - getDate(this._activeDate));
                break;
            case 'End':
                this.activeDate = addDays(this._activeDate, getDaysInMonth(this._activeDate) - getDate(this._activeDate));
                break;
            case 'PageUp':
                this.activeDate = event.altKey ? addYears(this._activeDate, -1) : addMonths(this._activeDate, -1);
                break;
            case 'PageDown':
                this.activeDate = event.altKey ? addYears(this._activeDate, 1) : addMonths(this._activeDate, 1);
                break;
            case 'Enter':
                if (!dateFilter || dateFilter(this._activeDate)) {
                    this._dateSelected(getDate(this._activeDate));
                    this._userSelection.emit(undefined);
                    // Prevent unexpected default actions such as form submission.
                    event.preventDefault();
                }
                return;
            default:
                // Don't prevent default or focus active cell on keys that we don't explicitly handle.
                return;
        }

        this._focusActiveCell();
        // Prevent unexpected default actions such as form submission.
        event.preventDefault();
    }

    /** Initializes this month view. */
    _init() {
        this._selectedDate = this._getDateInCurrentMonth(this.selected);
        this._todayDate = this._getDateInCurrentMonth(ApDateService.utc());
        this._monthLabel = this.monthNames[getMonth(this.activeDate)].toLocaleUpperCase();

        const firstOfMonth = ApDateService.utc(new Date(getYear(this.activeDate), getMonth(this.activeDate), 1));
        this._firstWeekOffset = (DAYS_PER_WEEK + getDay(firstOfMonth) - this.firstDayOfWeek) % DAYS_PER_WEEK;

        this._createWeekCells();
        this.changeDetectorRef.markForCheck();
    }

    /** Focuses the active cell after the microtask queue is empty. */
    _focusActiveCell() {
        this._apCalendarBody()._focusActiveCell();
    }

    /** Creates MatCalendarCells for the dates in this month. */
    private _createWeekCells() {
        const daysInMonth = getDaysInMonth(this.activeDate);
        const dateNames = this.range(31, (i) => ApDateService.format(ApDateService.utc(new Date(2017, 0, i + 1)), 'd'));
        this._weeks = [[]];
        for (let i = 0, cell = this._firstWeekOffset; i < daysInMonth; i++, cell++) {
            if (cell === DAYS_PER_WEEK) {
                this._weeks.push([]);
                cell = 0;
            }
            const date = ApDateService.utc(new Date(getYear(this.activeDate), getMonth(this.activeDate), i + 1));
            const enabled = this._shouldEnableDate(date);
            const ariaLabel = ApDateService.format(date, 'PPPP');
            this._weeks[this._weeks.length - 1].push(new ApCalendarCell(i + 1, dateNames[i], ariaLabel, enabled));
        }
    }

    private range<T>(length: number, valueFunction: (index: number) => T): T[] {
        const valuesArray = Array(length);
        for (let i = 0; i < length; i++) {
            valuesArray[i] = valueFunction(i);
        }
        return valuesArray;
    }

    /** Date filter for the month */
    private _shouldEnableDate(date: Date): boolean {
        const dateFilter = this.dateFilter();
        return (
            !!date &&
            (!dateFilter || dateFilter(date)) &&
            (!this.minDate() || !isAfter(this.minDate(), date)) &&
            (!this.maxDate() || !isBefore(this.maxDate(), date))
        );
    }

    /**
     * Gets the date in this month that the given Date falls on.
     * Returns null if the given Date is in another month.
     */
    private _getDateInCurrentMonth(date: Date | null): number | null {
        return date && this._hasSameMonthAndYear(date, this.activeDate) ? getDate(date) : null;
    }

    /** Checks whether the 2 dates are non-null and fall within the same month of the same year. */
    private _hasSameMonthAndYear(d1: Date | null, d2: Date | null): boolean {
        return d1 && d2 && getMonth(d1) === getMonth(d2) && getYear(d1) === getYear(d2);
    }

    /** Determines whether the user has the RTL layout direction. */
    private _isRtl() {
        return this.dir && this.dir.value === 'rtl';
    }
}
